/**
 * Übergangs-Effekt beim Theme- und Hell/Dunkel-Wechsel.
 *
 * WARUM ZENTRAL: Der Wechsel selbst liegt im ColorThemeProvider, also muss der
 * Effekt auch dorthin — sonst müsste ihn jede App einzeln nachbauen und die
 * Varianten liefen auseinander.
 *
 * WARUM MIT URSPRUNG: "wave" startet dort, wo der Zeiger zuletzt war. Damit ist
 * die Bewegung kausal — sie sagt "diese Änderung kam von diesem Schalter" —
 * statt bloß dekorativ zu sein. Der Umschalter sitzt am Desktop oben rechts und
 * mobil im unteren Menü; aus beiden Ecken liest sich das eindeutig.
 *
 * Die Zeigerposition wird global mitgeschrieben, damit die Aufrufer nichts
 * durchreichen müssen. Ist sie alt (> 2 s) oder gab es sie nie — Tastatur,
 * System-Dark-Mode, Profil-Sync, iframe-Bridge — fällt es auf eine Blende
 * zurück. Ohne diesen Fallback spränge die Welle aus Ecke (0,0) und sähe
 * schlicht kaputt aus.
 */

import { readThemeCookie, writeThemeCookie, THEME_TRANSITION_COOKIE } from "./theme-cookie";
import { prefersReducedMotion } from "./bewegung";

export type ThemeTransition = "none" | "soft" | "wave" | "diagonal";

export const THEME_TRANSITIONS: { id: ThemeTransition; label: string; hint: string }[] = [
  { id: "none", label: "Keine", hint: "Sofortiger Wechsel." },
  { id: "soft", label: "Weich", hint: "Farben blenden über." },
  { id: "wave", label: "Welle", hint: "Breitet sich vom Schalter aus." },
  { id: "diagonal", label: "Diagonal", hint: "Schräge Kante wischt durch." },
];

export const DEFAULT_THEME_TRANSITION: ThemeTransition = "wave";

/** localStorage-Key — exportiert fuer den storage-Listener (Same-Origin-Tabs). */
export const THEME_TRANSITION_LS_KEY = "janeway-theme-transition";
const LS_KEY = THEME_TRANSITION_LS_KEY;
/** Ab hier gilt die Zeigerposition als veraltet und der Ursprung als unbekannt. */
const POINTER_MAX_AGE_MS = 2000;

export function isThemeTransition(v: unknown): v is ThemeTransition {
  return v === "none" || v === "soft" || v === "wave" || v === "diagonal";
}

/**
 * Cookie hat Vorrang vor localStorage — GENAU wie beim Color-Theme.
 *
 * localStorage ist origin-gebunden: eine Wahl in ai-chat erreichte hub nie, dort
 * lief weiter der Default (Welle). Der geteilte Parent-Domain-Cookie ist der
 * einzige Speicher, den sich alle Subdomains teilen. Der localStorage-Wert bleibt
 * als Fallback fuer Kontexte ohne Cookie-Domain (lokale Entwicklung).
 */
export function readThemeTransition(): ThemeTransition {
  if (typeof window === "undefined") return DEFAULT_THEME_TRANSITION;
  const c = readThemeCookie(THEME_TRANSITION_COOKIE);
  if (isThemeTransition(c)) return c;
  const v = window.localStorage.getItem(LS_KEY);
  return isThemeTransition(v) ? v : DEFAULT_THEME_TRANSITION;
}

export function writeThemeTransition(v: ThemeTransition): void {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(LS_KEY, v);
  writeThemeCookie(THEME_TRANSITION_COOKIE, v);
}

// ── Zeigerposition ─────────────────────────────────────────────────────

let lastPointer: { x: number; y: number; t: number } | null = null;
let tracking = false;

/** Einmalig pro Dokument. Idempotent — mehrere Provider-Instanzen sind ok. */
export function trackPointerOrigin(): void {
  if (tracking || typeof window === "undefined") return;
  tracking = true;
  window.addEventListener(
    "pointerdown",
    (e) => {
      lastPointer = { x: e.clientX, y: e.clientY, t: Date.now() };
    },
    { capture: true, passive: true },
  );
}

/** Mittelpunkt des auslösenden Schalters — exakt und unabhängig davon, wo
 *  innerhalb der Schaltfläche getippt wurde. Ein Element ohne Ausdehnung
 *  (ausgehängt, display:none) liefert 0×0 und wird verworfen. */
function elementOrigin(el?: Element | null): { x: number; y: number } | null {
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width === 0 && r.height === 0) return null;
  return { x: r.left + r.width / 2, y: r.top + r.height / 2 };
}

function freshPointer(): { x: number; y: number } | null {
  if (!lastPointer) return null;
  if (Date.now() - lastPointer.t > POINTER_MAX_AGE_MS) return null;
  return { x: lastPointer.x, y: lastPointer.y };
}

// ── Ausführung ─────────────────────────────────────────────────────────

/** Pending Rückbau-Timer des letzten "soft"-Laufs — muss beim nächsten Lauf
 *  abgebrochen werden, sonst räumt er mitten in einem neuen Durchlauf ab. */
let softFxTimer: number | null = null;
/** Das `settle` (u.a. `setTheme`) des noch laufenden "soft"-Timers. Bricht ein
 *  ueberlappender zweiter Lauf den Timer ab, MUSS dieses settle vorher noch
 *  feuern — sonst geht der Nachlauf des ersten Laufs verloren und der Modus
 *  wird nie persistiert. "soft" setzt `activeWave` nicht, also faengt die
 *  Doppelklick-Sperre der Aufrufer diesen Fall nicht ab. */
let softFxSettle: (() => void) | null = null;

/**
 * ECHTER Reveal ueber die View-Transitions-API.
 *
 * Der Browser haelt den alten Zustand als Bild fest und blendet den neuen
 * darueber ein — auf eine wachsende Form geclippt. Der Kreis IST dadurch schon
 * das neue Theme, mit Text, Rahmen und Logo. Das ist der Effekt aus dem ux-lab.
 *
 * Nichts kann hier auseinanderlaufen: Es gibt keinen Klon, dessen Zustand vom
 * Original abweichen koennte. Beide Bilder macht der Browser selbst.
 *
 * `mix-blend-mode: normal` ist noetig, sonst laeuft die Standard-Kreuzblende
 * zusaetzlich mit und die Kante wirkt matschig.
 *
 * Gibt false zurueck, wenn die API fehlt — dann uebernimmt der Farb-Crossfade.
 */
function runViewTransitionReveal(
  from: string,
  to: string,
  dur: number,
  apply: () => void,
  settle: () => void,
): boolean {
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { finished: Promise<void> };
  };
  if (!doc.startViewTransition) return false;

  const name = `jwReveal${Date.now().toString(36)}`;
  const style = document.createElement("style");
  style.textContent =
    `::view-transition-old(root){animation:none;mix-blend-mode:normal}` +
    `::view-transition-new(root){animation:${name} ${dur}ms cubic-bezier(0.4,0,0.2,1) both;` +
    `mix-blend-mode:normal;z-index:1}` +
    `@keyframes ${name}{from{clip-path:${from}}to{clip-path:${to}}}`;
  document.head.appendChild(style);

  // Auch hier gegen Doppelklick sperren — sonst startet ein zweiter Wechsel
  // mitten in der laufenden Transition. `settle` gehoert IN release: wird die
  // Transition von aussen ueber activeWave() abgebrochen, muss der Nachlauf
  // sofort laufen, nicht erst verzoegert ueber t.finished — sonst koennte ein
  // bereits committeter zweiter Wechsel danach vom verspaeteten setTheme des
  // ersten zurueckgekippt werden. settle ist idempotent, ein doppelter Aufruf
  // (Abbruch + regulaeres t.finished) ist damit harmlos.
  const release = () => {
    if (activeWave === release) activeWave = null;
    style.remove();
    settle();
  };
  activeWave = release;

  const t = doc.startViewTransition(apply);
  void t.finished.catch(() => undefined).finally(release);
  return true;
}

let activeWave: (() => void) | null = null;

/** Laeuft gerade eine bewegte Transition (Desktop-View-Transition oder
 *  Mobil-Klon)? Waehrenddessen darf nicht erneut umgeschaltet werden — ein
 *  Abbruch muesste die laufende hart auf ihr Ziel schnappen. */
export function isThemeTransitionBusy(): boolean {
  return activeWave !== null;
}

/** Setzt Stile einzeln statt per cssText: ein einziger nicht unterstuetzter Wert
 *  verwirft dort je nach CSS-Parser den GESAMTEN Block, und das Element staende
 *  voellig ohne Stil da (in der Testumgebung reproduziert). */
function applyStyles(el: HTMLElement, styles: Record<string, string>): void {
  for (const [k, v] of Object.entries(styles)) {
    (el.style as unknown as Record<string, string>)[k] = v;
  }
}

/** Mobil-Breakpoint — deckungsgleich mit der `@media (max-width: 768px)`-Regel
 *  in globals.css, die `html/body` auf `position: fixed` setzt. Genau dort
 *  verschiebt der Browser die View-Transition (per Messung belegt: Koordinaten
 *  korrekt, Viewport stabil, dennoch falsche Position — Compositing bei dpr>1),
 *  weshalb Mobil einen eigenen, snapshot-freien Pfad braucht. */
function isMobileViewport(): boolean {
  return typeof window !== "undefined" && window.innerWidth <= 768;
}

/**
 * Enthaelt die Seite ein iframe, dessen Inhalt der Browser nicht in den
 * View-Transition-Schnappschuss aufnehmen darf?
 *
 * Trifft auf fremde Herkunft zu — und ebenso auf `sandbox="allow-scripts"` OHNE
 * `allow-same-origin`, weil das einen intransparenten Ursprung erzeugt. Genau so
 * rendert ai-chat seine Artefakte (bewusst, aus Sicherheitsgruenden).
 *
 * Folge ohne diese Pruefung: `startViewTransition()` laeuft, der Browser
 * ueberspringt sie aber still. `runViewTransitionReveal` meldet trotzdem Erfolg,
 * der Aufrufer nimmt deshalb NICHT den Ersatz — und das Theme springt hart um,
 * ganz ohne Animation. Genau das war bei geoeffnetem Artefakt-Panel zu sehen.
 *
 * Erkennung ueber den Zugriff auf `contentDocument`: bei fremder Herkunft wirft
 * er oder liefert null. Ein leeres iframe (noch nicht geladen) liefert ein
 * Dokument und gilt damit korrekt als unproblematisch.
 */
function hasUnsnapshottableIframe(): boolean {
  if (typeof document === "undefined") return false;
  for (const f of Array.from(document.getElementsByTagName("iframe"))) {
    // Unsichtbare iframes (Analytics, Prefetch) stoeren den Schnappschuss nicht.
    if (f.offsetWidth === 0 && f.offsetHeight === 0) continue;
    try {
      if (f.contentDocument === null) return true;
    } catch {
      return true;
    }
  }
  return false;
}

/**
 * Laeuft auf der Seite gerade ein Live-Video (Kamera, Bildschirmfreigabe)?
 *
 * Das entscheidet, ob ein Theme-Wechsel ueberhaupt mit Schnappschuessen
 * arbeiten darf. Beide schnappschussbasierten Wege zeigen Live-Video NICHT:
 *
 * - `runCloneReveal` (Mobil) klont den `<body>` per `cloneNode(true)`. Die
 *   Quelle eines Live-Videos haengt aber an der **Eigenschaft** `srcObject`,
 *   nicht an einem Attribut — `cloneNode` kopiert sie deshalb nicht. Im Klon
 *   sind alle Videokacheln leer.
 * - Die View-Transition legt ein Standbild ueber die Seite; Chromium
 *   komponiert Video auf Mobil haeufig auf einer eigenen Ebene, die in diesem
 *   Standbild fehlt.
 *
 * Gemeldet am 2026-08-18 aus einem Meeting am Handy: beim Umschalten von hell
 * auf dunkel verschwanden Kameras und Bildschirmfreigaben kurz. Fuer die halbe
 * Sekunde, die der Effekt laeuft, sah man leere Kacheln.
 *
 * Ausschlaggebend ist `srcObject`. Ein `<video src="…">` (Datei) ueberlebt den
 * Klon, weil `src` ein Attribut ist — nur der Medienstrom tut es nicht.
 */
function hasLiveVideo(): boolean {
  if (typeof document === "undefined") return false;
  for (const v of Array.from(document.getElementsByTagName("video"))) {
    if (!v.srcObject) continue;
    // Unsichtbare Elemente (vorgeladen, ausgeblendet) stoeren niemanden.
    if (v.offsetWidth === 0 && v.offsetHeight === 0) continue;
    return true;
  }
  return false;
}

// ── Weicher Uebergang ("soft") ──────────────────────────────────────────
// Bevorzugt ein GPU-Crossfade ueber die View-Transition (opacity ist
// compositor-beschleunigt → fluessig). Nur ohne die API der CSS-Farb-Uebergang
// als Fallback (transitioniert die Farbe JEDES Elements — reine Paint-Arbeit,
// ruckelt auf Mobil, weil pro Frame die halbe Seite neu gezeichnet wird).

/** Crossfade-Dauer (GPU-Pfad) — snappy, weil compositor. */
const SOFT_VT_MS = 300;
/** Dauer, nach der das Uebergangs-Attribut des CSS-Fallbacks wieder entfernt
 *  wird. Muss zur transition-duration in globals.css (400ms) passen, mit Puffer. */
const SOFT_MS = 460;

/** GPU-Crossfade: die neue Seite blendet per opacity ein, die alte bleibt
 *  statisch darunter. Kein clip-path, kein Ursprung → keine dpr>1-Verschiebung,
 *  laeuft daher auch auf Mobil sauber. Ueberschreibt die globale
 *  `::view-transition-*{animation:none}`-Regel nur fuer diesen Lauf. Gibt false
 *  zurueck, wenn die API fehlt. */
function runViewTransitionFade(dur: number, apply: () => void, settle: () => void): boolean {
  const doc = document as Document & {
    startViewTransition?: (cb: () => void) => { finished: Promise<void> };
  };
  if (!doc.startViewTransition) return false;

  const style = document.createElement("style");
  style.textContent =
    `::view-transition-old(root){animation:none}` +
    `::view-transition-new(root){animation:jwSoftFade ${dur}ms ease both;mix-blend-mode:normal;z-index:1}` +
    `@keyframes jwSoftFade{from{opacity:0}to{opacity:1}}`;
  document.head.appendChild(style);

  const release = () => {
    if (activeWave === release) activeWave = null;
    style.remove();
    settle();
  };
  activeWave = release;

  const t = doc.startViewTransition(apply);
  void t.finished.catch(() => undefined).finally(release);
  return true;
}

function runSoftFade(root: HTMLElement, apply: () => void, settle: () => void): void {
  // GPU-Crossfade wenn moeglich (fluessig); sonst CSS-Farb-Uebergang.
  // Bei einem nicht snapshotbaren iframe ueberspringt der Browser AUCH diesen
  // View-Transition-Weg still — dann direkt in den CSS-Uebergang, der ohne
  // Schnappschuss auskommt. Sonst bliebe wieder nur ein harter Sprung.
  // Live-Video kommt in keinen Schnappschuss (siehe `hasLiveVideo`) -- dann
  // bleibt nur der CSS-Farb-Uebergang, der ohne Schnappschuss auskommt.
  if (!hasUnsnapshottableIframe() && !hasLiveVideo() && runViewTransitionFade(SOFT_VT_MS, apply, settle)) return;

  // Ueberlappender zweiter Lauf: den Timer des ersten stoppen und dessen settle
  // (u.a. setTheme) nachholen, sonst geht der Nachlauf verloren und der Modus
  // wird nie persistiert. "soft" setzt activeWave NICHT, die Doppelklick-Sperre
  // der Aufrufer greift hier also nicht.
  if (softFxTimer !== null) {
    window.clearTimeout(softFxTimer);
    softFxSettle?.();
  }
  root.dataset.themeFx = "soft";
  apply();
  softFxSettle = settle;
  softFxTimer = window.setTimeout(() => {
    delete root.dataset.themeFx;
    softFxTimer = null;
    softFxSettle = null;
    settle();
  }, SOFT_MS);
}

// ── Klon-Reveal (Mobil) ──────────────────────────────────────────────────
//
// WARUM KLON STATT VIEW-TRANSITION AUF MOBIL: Die View-Transition wird von
// Chromium bei dpr>1 verschoben (per Geraete-Messung belegt: uebergebene
// clip-path-Koordinaten korrekt, Viewport stabil, Position dennoch falsch —
// passiert in der Compositing-Ebene, fuer JS unerreichbar). Der Klon dagegen ist
// ein normales Element, dessen Position WIR setzen — keine Verschiebung.
//
// WARUM EINGEFROREN: Der Klon ist eine LEBENDE DOM-Kopie. Beim Einhaengen
// spielt jedes Element mit CSS-Animation sie NEU ab — ai-chat hat
// `animate-message-in` (Fade von opacity:0 + Slide) auf jeder Nachricht, die
// dadurch im Kreis erneut einfaden und -sliden: das war der sichtbare Bug
// ("zappeln", "Farbe hin und her", "Chats animieren neu"). Der Desktop-Reveal
// hat das nicht, weil die View-Transition ein RASTER-Bild ist (Bitmap animiert
// nicht). Deshalb wird der Klon per eingehaengtem <style> zum Standbild
// eingefroren: `animation:none; transition:none` auf allem darin. Da alle
// Entrance-Animationen zum Umschalt-Zeitpunkt bereits fertig sind (Nachrichten
// stehen still), zeigt der eingefrorene Basiszustand exakt das aktuelle Bild —
// wie das Raster-Snapshot. Das ersetzt die frueher aufgegebene, per-Element
// arbeitende `freezeAnimationsToLiveState`-Maschinerie durch EINE CSS-Regel.
//
// WARUM COUNTER-SCALE: Ein Fenster fester Groesse waechst per `transform:scale()`
// (compositor, kein Ruckeln), sein Inhalt skaliert exakt gegenlaeufig zurueck und
// bleibt ortsfest. clip-path waere nicht compositor-faehig und ruckelte.
// Gegenskalierung uniform in s abgetastet (Ease-Out im Zeit-Offset via cbrt),
// sonst zoomt der Inhalt zwischen den Stuetzstellen.

const CLONE_ID = "jw-theme-clone";
const CLONE_FREEZE_STYLE_ID = "jw-theme-clone-freeze";
/** Kleinster Startwert. Nicht 0: die Gegenskalierung 1/s liefe dort gegen
 *  unendlich und der Browser muesste eine absurd grosse Textur vorhalten. */
const CLONE_START_SCALE = 0.06;
const CLONE_SAMPLES = 60;

/** Uebertraegt Scrollpositionen und Formularwerte, die cloneNode NICHT mitnimmt.
 *  Paarung per Index; beide Listen schliessen script/noscript aus, damit von
 *  Next.js mitten in den Body gestreamte `<script>__next_f`-Tags die Paarung
 *  nicht verschieben. `source` ist document.body inkl. Overlay — dessen Elemente
 *  liegen hinter allen Originalen und jenseits von `n`. */
function carryOverState(source: HTMLElement, clone: HTMLElement): void {
  const sel = ":not(script):not(noscript)";
  const a = source.querySelectorAll<HTMLElement>(sel);
  const b = clone.querySelectorAll<HTMLElement>(sel);
  const n = Math.min(a.length, b.length);
  for (let i = 0; i < n; i++) {
    const s = a[i];
    const c = b[i];
    if (s.scrollTop) c.scrollTop = s.scrollTop;
    if (s.scrollLeft) c.scrollLeft = s.scrollLeft;
    if (s instanceof HTMLInputElement && c instanceof HTMLInputElement) c.value = s.value;
    else if (s instanceof HTMLTextAreaElement && c instanceof HTMLTextAreaElement) c.value = s.value;
  }
}

function runCloneReveal(
  o: { x: number; y: number },
  apply: () => void,
  settle: () => void,
  nextVars: Record<string, string>,
  nextDark: boolean,
): void {
  // Aus der GERENDERTEN Viewport-Box, nicht aus innerWidth/innerHeight. Die echte
  // Seite ist `height: 100dvh` (= documentElement-Box), waehrend `innerHeight` auf
  // Mobil davon abweicht (gemessen: dvh 750 vs innerHeight 749). AppLayout wurzelt
  // in `h-full`, also verschiebt schon 1px die ganze bodenverankerte Spalte — der
  // Klon lag dadurch minimal anders als die echte Seite und schnappte beim Abbau.
  // `|| innerWidth/Height` faengt jsdom (rect=0) und degenerierte Faelle ab.
  const rect = document.documentElement.getBoundingClientRect();
  const w = rect.width || window.innerWidth;
  const h = rect.height || window.innerHeight;
  const r = Math.hypot(Math.max(o.x, w - o.x), Math.max(o.y, h - o.y)) + 4;
  const dur = Math.round(Math.min(680, Math.max(380, 300 + r * 0.2)));
  const s0 = CLONE_START_SCALE;

  // Einfrier-Regel: schaltet JEDE Animation/Transition im Klon ab, sodass er ein
  // Standbild ist statt Entrance-Animationen neu abzuspielen. Auf #jw-theme-clone
  // gescoped — die echte Seite bleibt unberuehrt.
  const freeze = document.createElement("style");
  freeze.id = CLONE_FREEZE_STYLE_ID;
  freeze.textContent =
    `#${CLONE_ID},#${CLONE_ID} *,#${CLONE_ID} *::before,#${CLONE_ID} *::after{` +
    `animation:none!important;transition:none!important}`;
  document.head.appendChild(freeze);

  const win = document.createElement("div");
  win.id = CLONE_ID;
  win.setAttribute("aria-hidden", "true");
  applyStyles(win, {
    position: "fixed",
    left: `${Math.round(o.x - r)}px`,
    top: `${Math.round(o.y - r)}px`,
    width: `${Math.round(r * 2)}px`,
    height: `${Math.round(r * 2)}px`,
    borderRadius: "50%",
    overflow: "hidden",
    pointerEvents: "none",
    zIndex: "2147483647",
    willChange: "transform",
    transform: `scale(${s0})`,
  });

  const inner = document.createElement("div");
  applyStyles(inner, {
    position: "absolute",
    left: `${Math.round(-(o.x - r))}px`,
    top: `${Math.round(-(o.y - r))}px`,
    width: `${w}px`,
    height: `${h}px`,
    overflow: "hidden",
    transformOrigin: `${o.x}px ${o.y}px`,
    willChange: "transform",
    transform: `scale(${1 / s0})`,
  });
  for (const [k, v] of Object.entries(nextVars)) inner.style.setProperty(`--${k}`, v);
  inner.style.background = nextVars.background ?? "";
  inner.style.color = nextVars.foreground ?? "";
  inner.classList.toggle("dark", nextDark);

  const clone = document.body.cloneNode(true) as HTMLElement;
  clone.querySelectorAll("script,noscript").forEach((el) => el.remove());
  clone.setAttribute("inert", "");
  clone.setAttribute("aria-hidden", "true");
  clone.style.width = `${w}px`;
  clone.style.height = `${h}px`;
  clone.style.margin = "0";

  inner.appendChild(clone);
  win.appendChild(inner);
  document.body.appendChild(win);
  carryOverState(document.body, clone);

  // Uniform in s; Ease-Out im Zeit-Offset 1-(1-p)^(1/3). Beide Achsen teilen den
  // Offset → Fenster·Inhalt an jeder Stuetzstelle exakt 1, dazwischen <1.4%.
  const winFrames: Keyframe[] = [];
  const innerFrames: Keyframe[] = [];
  for (let i = 0; i <= CLONE_SAMPLES; i++) {
    const p = i / CLONE_SAMPLES;
    const s = s0 + (1 - s0) * p;
    const offset = 1 - Math.cbrt(1 - p);
    winFrames.push({ transform: `scale(${s})`, offset });
    innerFrames.push({ transform: `scale(${1 / s})`, offset });
  }
  const timing: KeyframeAnimationOptions = { duration: dur, easing: "linear", fill: "forwards" };

  let settled = false;
  const finish = () => {
    if (settled) return;
    settled = true;
    window.clearTimeout(watchdog);

    // REIHENFOLGE ist der Kern: Der Kreis deckt bei scale 1 den GANZEN Viewport
    // (Radius reicht zur entferntesten Ecke). Wir schalten die echte Seite JETZT
    // komplett um — Farben UND React-Inhalt — und lassen sie fertig neu zeichnen,
    // waehrend der Kreis noch alles verdeckt. Erst DANACH decken wir auf.
    //
    // Vorher wurde der Kreis SOFORT entfernt und danach setTheme() gerufen: die
    // echte Seite wurde erst nach dem Aufdecken von React neu gerendert — genau
    // das sichtbare "Texte und Elemente rendern am Ende". Jetzt passiert das
    // Nachrendern verdeckt.
    //
    // `noTx` unterdrueckt dabei die Tailwind-`transition-colors` (~150ms, ai-chat
    // 237 Stellen), die sonst von ALT→NEU animierten und alte Farbe aufblitzen
    // liessen (wie next-themes' disableTransitionOnChange).
    const noTx = document.createElement("style");
    noTx.textContent = "*,*::before,*::after{transition:none!important}";
    document.head.appendChild(noTx);
    apply(); // Farben instant neu
    void document.body.offsetHeight; // Reflow: neue Werte ohne Transition committen
    settle(); // setTheme → React rendert den Inhalt neu, HINTER dem deckenden Kreis

    // Zwei Frames: React committet + Browser zeichnet die neue Seite fertig hinter
    // dem Kreis. Dann erst aufdecken → kein sichtbares Nachrendern. Auch bei
    // Ungleichzeitigkeit unkritisch, weil Kreis und echte Seite dann dasselbe
    // neue Theme zeigen.
    const reveal = () => {
      win.remove();
      freeze.remove();
      noTx.remove();
      activeWave = null;
    };
    requestAnimationFrame(() => requestAnimationFrame(reveal));
  };
  // Solange der Kreis noch verdeckt (bis reveal), einen erneuten Wechsel sauber
  // abschliessen lassen: activeWave zeigt bis dahin auf ein sofortiges Aufdecken.
  activeWave = finish;
  const watchdog = window.setTimeout(finish, dur + 2000);

  const grow = win.animate(winFrames, timing);
  inner.animate(innerFrames, timing);
  grow.finished.then(finish, finish);
}

/**
 * Führt `apply` aus und legt den gewählten Übergang darüber.
 *
 * `apply` MUSS den DOM synchron verändern (CSS-Variablen direkt schreiben,
 * nicht nur React-State setzen): startViewTransition schnappt den DOM im
 * Callback. Ein useEffect, der die Variablen erst nach dem Paint schreibt,
 * käme zu spät und die Transition zeigte zweimal denselben Zustand.
 */
export function runThemeTransition(
  fx: ThemeTransition,
  apply: () => void,
  /**
   * Auslösendes Element. Wenn vorhanden, ist ES der Ursprung — nicht der
   * globale Zeiger-Mitschnitt. Das ist der verlässliche Weg: in Menüs (Radix &
   * Co.) wird der Schalter teils über synthetische Events aktiviert, ohne dass
   * auf IHM ein pointerdown landet. Dann bliebe der letzte Mitschnitt stehen —
   * der vom Öffnen des Menüs — und die Welle liefe aus der falschen Ecke los.
   */
  originEl?: Element | null,
  /** Variablen des ZIEL-Themes (ohne `--`) plus Zielmodus — der Klon im Kreis
   *  wird damit eingefaerbt. Fehlen sie, faellt es auf Sofortwechsel zurueck. */
  nextVars?: Record<string, string>,
  nextDark?: boolean,
  /**
   * Laeuft ERST, wenn der Uebergang optisch durch ist.
   *
   * Hier gehoert alles hinein, was den Hauptthread beschaeftigt und optisch
   * nichts mehr beitraegt — allen voran `setTheme()` von next-themes. Die
   * clip-path-Animation der View-Transition wird vom Hauptthread getrieben;
   * laeuft eine Style-Neuberechnung des ganzen Dokuments hinein, verschluckt
   * sie ihre ersten Frames. Genau das war sichtbar: der Kreis erschien erst
   * auf halbem Weg.
   */
  onSettled?: () => void,
): void {
  const root = typeof document !== "undefined" ? document.documentElement : null;

  // Genau einmal — jeder Endpfad ruft es, mehrere duerfen sich ueberschneiden
  // (Abbruch von aussen + regulaeres Ende).
  let didSettle = false;
  const settle = () => {
    if (didSettle) return;
    didSettle = true;
    onSettled?.();
  };

  // Laufenden Wellen-Durchgang IMMER zuerst abschliessen — nicht erst in
  // runWaveOverlay. Sonst laeuft eine noch animierende Welle weiter, waehrend
  // ein Wechsel ueber einen anderen Pfad (keine/weich/diagonal, oder Welle ohne
  // Ursprung) sofort anwendet; ihr spaeteres apply() ueberschreibt dann den
  // frisch gesetzten Zustand mit seinem veralteten Ziel — und persistiert ihn
  // ueber setTheme() sogar nach Supabase. Ausloeser ist realistisch: Effekt-
  // Auswahl und Farbkacheln sitzen im selben Design-Tab.
  if (activeWave) activeWave();

  if (!root || fx === "none" || prefersReducedMotion()) {
    apply();
    settle();
    return;
  }

  // "soft" — reiner Farb-Crossfade, keine View-Transition. Details in runSoftFade.
  if (fx === "soft") {
    runSoftFade(root, apply, settle);
    return;
  }

  // Ein nicht snapshotbares iframe (ai-chat-Artefakte laufen sandboxed ohne
  // allow-same-origin) laesst den Browser JEDE View-Transition still
  // ueberspringen — Welle wie Diagonale. Der Farb-Crossfade kommt ohne
  // Schnappschuss aus und bleibt als einziger Weg uebrig; die iframe-Inhalte
  // faerben sich ueber ihre eigene Theme-Bruecke mit um.
  if (hasUnsnapshottableIframe()) {
    runSoftFade(root, apply, settle);
    return;
  }

  // Laeuft ein Live-Video, faellt JEDER schnappschussbasierte Weg aus -- Welle,
  // Diagonale und der mobile Klon-Reveal gleichermassen. `runSoftFade` merkt
  // dasselbe noch einmal und nimmt dann den CSS-Farb-Uebergang. Der Effekt ist
  // waehrend eines Gespraechs ohnehin die falsche Buehne; wichtiger ist, dass
  // die Gesichter stehen bleiben.
  if (hasLiveVideo()) {
    runSoftFade(root, apply, settle);
    return;
  }

  if (fx === "wave") {
    // MOBIL: fester Ursprung unten-Mitte, eingefrorener Klon-Reveal. Kein
    // Browser-Snapshot → keine dpr>1-Verschiebung; eingefroren → kein
    // Animation-Replay. Ursprung aus der GERENDERTEN Box (wie im Klon), damit der
    // Kreis-Mittelpunkt am echten unteren Rand (100dvh) sitzt, nicht 1px darueber.
    // Ohne Zielfarben (kein nextVars) faellt es auf den Farb-Crossfade zurueck.
    if (isMobileViewport()) {
      if (nextVars) {
        const rect = document.documentElement.getBoundingClientRect();
        const cw = rect.width || window.innerWidth;
        const ch = rect.height || window.innerHeight;
        runCloneReveal({ x: Math.round(cw / 2), y: ch }, apply, settle, nextVars, nextDark === true);
      } else {
        runSoftFade(root, apply, settle);
      }
      return;
    }

    // DESKTOP: nativer View-Transition-Reveal von der Klickstelle — artefaktfrei.
    const w = window.innerWidth;
    const h = window.innerHeight;
    const o = elementOrigin(originEl) ?? freshPointer();
    if (!o) {
      apply();
      settle();
      return;
    }
    const r = Math.round(Math.hypot(Math.max(o.x, w - o.x), Math.max(o.y, h - o.y)) + 4);
    const x = Math.round(o.x);
    const y = Math.round(o.y);
    const dur = Math.round(Math.min(620, Math.max(360, 280 + r * 0.2)));
    if (
      !runViewTransitionReveal(
        `circle(0px at ${x}px ${y}px)`,
        `circle(${r}px at ${x}px ${y}px)`,
        dur,
        apply,
        settle,
      )
    ) {
      // Browser ohne View-Transitions-API (selten, alte Desktops): sauberer
      // Farb-Crossfade statt eines eigenen Overlays.
      runSoftFade(root, apply, settle);
    }
    return;
  }

  // Diagonale: Desktop UND Mobil ueber die View-Transition. Anders als die Welle
  // hat sie KEINEN festen Startpunkt — die Kante wischt von Ecke zu Ecke durch,
  // also faellt die dpr>1-Snapshot-Verschiebung, die den Welle-Kreis fehlplaziert
  // haette, hier nicht auf. Beide Keyframes brauchen dieselbe Punktzahl, sonst
  // interpoliert clip-path nicht und die Kante springt.
  const diagDur = Math.round(
    Math.min(620, Math.max(360, 280 + Math.hypot(window.innerWidth, window.innerHeight) * 0.16)),
  );
  if (
    !runViewTransitionReveal(
      "polygon(0% 0%, 0% 0%, -30% 100%, 0% 100%)",
      "polygon(0% 0%, 130% 0%, 100% 100%, 0% 100%)",
      diagDur,
      apply,
      settle,
    )
  ) {
    // Browser ohne View-Transitions-API: Farb-Crossfade (s. Welle-Fallback).
    runSoftFade(root, apply, settle);
  }
}
